import { defineStore } from 'pinia';
import { getCookie, setCookie, deleteCookie } from '../../utils/cookies';
import { api } from '../../plugins/axios';
import { marketplacesOptions } from '../../utils/marketplaces';
import { useChatsListStore } from '../../modules/whatsapp/stores/chats-list';
import { useChatsStore } from '../../modules/whatsapp/stores/chats';
import { WhatsAppService } from '@/v2/modules/whatsapp/service';
import { TenantSetting } from '@/v2/modules/core/entities/tenant-settings';
import { getAuth } from 'firebase/auth';
import { featureFlag, isFeatureFlagEnabled } from '@/v2/entities/feature-flag';
import { AgentEcommerceService } from '@/v2/modules/agent-ecommerce/service';

export const useAuthStore = defineStore('auth', {
	state: () => {
		return {
			token: getCookie('token') || undefined,
			user: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : undefined,
			permissions: new Set(),
			subscription: localStorage.getItem('subscription')
				? JSON.parse(localStorage.getItem('subscription'))
				: undefined,
			linkedMarketPlaces: localStorage.getItem('linkedMarketPlaces')
				? JSON.parse(localStorage.getItem('linkedMarketPlaces'))
				: [],
			linkedMarketplacesList: localStorage.getItem('linkedMarketplacesList')
				? JSON.parse(localStorage.getItem('linkedMarketplacesList'))
				: [],
			meta: localStorage.getItem('meta') ? JSON.parse(localStorage.getItem('meta')) : {},
			featureFlags: localStorage.getItem('featureFlags')
				? JSON.parse(localStorage.getItem('featureFlags'))
				: [],
			fcmToken: localStorage.getItem('fcmToken') ?? undefined,
			firebaseTenantConfig: {},
			firebaseTenantSettings: {},
         tenantSettings: localStorage.getItem('tenantSettings')
            ? JSON.parse(localStorage.getItem('tenantSettings'))
            : {},
         termsAcceptanceStatus: null,
			registration_status: localStorage.getItem('registration_status')
				? JSON.parse(localStorage.getItem('registration_status'))
				: null,
			fetchRegistrationStatusPromise: null,
	};
},
	getters: {
		isLoggedIn(state) {
			return !!state.token;
		},
		hasWhatsappMarketplace(state) {
			const WHATSAPP_ID = 8;
			return state.linkedMarketPlaces.some((id) => id === WHATSAPP_ID);
		},
		amazonLinkedMarketplaces(state) {
			const AMAZON_ID = 1;
			return (state.linkedMarketplacesList || []).filter((lm) => lm.marketplace_id === AMAZON_ID);
		},
		isOwner(state) {
			return state.user?.is_owner;
		},
		availableMarketplaces(state) {
			return marketplacesOptions.filter((mp) =>
				state.linkedMarketPlaces.some((id) => id === mp.id),
			);
		},
		availableLinkedMarketplaces(state) {
			return (state.linkedMarketplacesList || []).map((lm) => {
				const label = lm.store_name ? `${lm.name} (${lm.store_name})` : lm.name;
				return { id: lm.id, label: label || String(lm.id) };
			});
		},
		isSubscriptionEnded(state) {
			return state.subscription?.is_subscribe_ended;
		},
		tenantId(state) {
			return state.user?.tenant?.id;
		},
		isSyncDisabled(state) {
			return !state.tenantSettings.some(
				(setting) =>
					setting.id == TenantSetting.PUSH_UPDATE_TO_MARKETPLACES &&
					Boolean(parseInt(setting.value)),
			);
		},
      isAgentSubscriptionExpired(state) {
         return Boolean(state.agentSubscription?.subscription?.is_expired);
      },
	},
	actions: {
		setUser(user) {
			localStorage.setItem('user', JSON.stringify(user));
			this.user = user;

			if (user?.permissions && Array.isArray(user.permissions)) {
				this.permissions = new Set(user.permissions);
			}
		},
		setLinkedMarketPlaces(linkedMarketPlaces) {
			localStorage.setItem('linkedMarketPlaces', JSON.stringify(linkedMarketPlaces));
			this.linkedMarketPlaces = linkedMarketPlaces;
		},
		setLinkedMarketplacesList(linkedMarketplacesList) {
			localStorage.setItem('linkedMarketplacesList', JSON.stringify(linkedMarketplacesList || []));
			this.linkedMarketplacesList = linkedMarketplacesList || [];
		},
		setSubscription(subscription) {
			localStorage.setItem('subscription', JSON.stringify(subscription));
			this.subscription = subscription;
		},
		setAgentSubscription(agentSubscription) {
			localStorage.setItem('agentSubscription', JSON.stringify(agentSubscription));
			this.agentSubscription = agentSubscription;
		},
		setAuthToken(token) {
			setCookie('token', token, 1000 * 1000);
			this.token = token;
		},
		setMeta(meta) {
         console.log('[AUTH_STORE] setMeta', meta);
			localStorage.setItem('meta', JSON.stringify(meta));
			this.meta = meta;
		},
		setTenantSettings(settings) {
			localStorage.setItem('tenantSettings', JSON.stringify(settings));
			this.tenantSettings = settings;
		},
      setFeatureFlags(flags) {
         localStorage.setItem('featureFlags', JSON.stringify(flags));
         this.featureFlags = flags;
      },
      setTermsAcceptanceStatus(status) {
         this.termsAcceptanceStatus = status;
      },
		setRegistrationStatus(status) {
			localStorage.setItem('registration_status', JSON.stringify(status || 'null'));
			this.registration_status = status;
		},
		async fetchRegistrationStatus(force = false) {
			if (!this.isLoggedIn) return null;

			if (!force && this.user) {
				console.log('[AUTH_STORE] Returning cached registration status');
				return this.registration_status;
			}

			if (this.fetchRegistrationStatusPromise) {
				console.log('[AUTH_STORE] Registration status fetch already in progress, returning existing promise');
				return this.fetchRegistrationStatusPromise;
			}

			console.log('[AUTH_STORE] Fetching registration status via fetchUser...');
			this.fetchRegistrationStatusPromise = this.fetchUser(true)
				.then(() => {
					return this.registration_status;
				})
				.catch((error) => {
					console.error('[AUTH_STORE] Failed to fetch registration status', error);
					throw error;
				})
				.finally(() => {
					this.fetchRegistrationStatusPromise = null;
				});

			return this.fetchRegistrationStatusPromise;
		},
		async fetchUser(force = false, refresh = false) {
			if (this.user && !force) return;

			try {
				const params = {
					fcm_token: this.fcmToken,
					firestore_token: true,
				};
            const meData = (await api.get('/user/auth/me', { params })).data.result;
            const user = meData?.user;
            const linkedMarketPlaces = meData?.linked_marketplaces;
            const linkedMarketplacesList = meData?.linked_marketplaces_list;
            const subscription = meData?.subscription;
            const agentSubscription = meData?.agent_subscription;
            const featureFlags = meData?.feature_flags;
            const tenantSettings = meData?.tenant_settings;
            const termsAcceptanceStatus = meData?.terms_acceptance_status;
            const registrationStatus = meData?.registration_status;

            this.setUser(user);
            this.setLinkedMarketPlaces(linkedMarketPlaces);
            this.setLinkedMarketplacesList(linkedMarketplacesList);
            this.setSubscription(subscription);
            this.setAgentSubscription(agentSubscription);
            this.setFeatureFlags(featureFlags);
            this.setTenantSettings(tenantSettings);
            this.setTermsAcceptanceStatus(termsAcceptanceStatus);
            this.setRegistrationStatus(registrationStatus);
				const chatsStore = useChatsStore();
				const chatsListStore = useChatsListStore();

            const meta = {
					fireStoreToken: meData.firestore_token,
					isZatcaLinked: meData.is_zatca_linked,
				};
				this.setMeta(meta);

				if (!refresh) {
					chatsStore.authenticateWithFirebase().then(() => {
						chatsListStore.watchNewConversations();
						chatsListStore.watchAiEscalation();
						this.fetchFirebaseTenantConfig();

						if (isFeatureFlagEnabled(featureFlag.ECOMMERCE_AI)) {
							this.fetchFirebaseTenantSettings();
						}
					});
				}

			} catch (err) {
				console.error(err);
				await this.logout();
				location.href = '/store/logout';
			}
		},
		async isDatabaseCreated() {
			let data = null;
			try {
				data = (await api.get('/user/auth/me', { params: { firestore_token: true } })).data?.result;
				console.log('[AUTH_STORE] isDatabaseCreated data', data);

            if (data?.firestore_token) {
               const meta = {
                  fireStoreToken: data.firestore_token,
                  isZatcaLinked: data.is_zatca_linked,
               };
               this.setMeta(meta);
            }
			} catch (e) {
				console.error(e);
				return false;
			}
            const isMigrated = data?.user?.tenant?.is_migrated;
            const isFirebaseInitialized = data?.is_firebase_initialized;
            const packageProduct = data?.package_product;
            this.packageProduct = packageProduct;

            console.log('[AUTH_STORE] Checking database creation:', {
                isMigrated,
                isFirebaseInitialized,
                packageProduct
			});

            // For agent subscriptions, wait for both database migration AND Firebase initialization
            if (packageProduct === 'linker_agents') {
                console.log('[AUTH_STORE] Agent subscription - waiting for Firebase initialization');
                return Boolean(isMigrated && isFirebaseInitialized);
            }

            // For non-agent products, only check migration
            console.log('[AUTH_STORE] Non-agent subscription - checking migration only');
            return Boolean(isMigrated);
		},
		async checkAndResumeRegistration() {
			if (!this.isLoggedIn) return null;

			try {
				const status = await this.fetchRegistrationStatus();

				if (status?.has_incomplete_registration) {
					return {
                  ...status,
						shouldResume: true,
						resumeUrl: status.resume_url,
						currentStep: status.current_step,
					};
				}

				return null;
			} catch (e) {
				console.error('Failed to check registration status', e);
				return null;
			}
		},
		async fetchFirebaseTenantConfig() {
			const data = await WhatsAppService.getFirebaseTenantConfig();
			this.firebaseTenantConfig = data;
		},
		async fetchFirebaseTenantSettings() {
			const data = await AgentEcommerceService.getGeneralSettings();
			this.firebaseTenantSettings = data;
		},
		async logout() {
			try {
				await api.get('/user/auth/logout');
			} catch (err) {
				console.error(err);
			} finally {
            this.token = undefined;
            this.user = {};
            this.linkedMarketPlaces = [];
            this.linkedMarketplacesList = [];
            this.registration_status = null;
            this.fetchRegistrationStatusPromise = null;
            localStorage.removeItem('user');
            localStorage.removeItem('token');
            localStorage.removeItem('linkedMarketPlaces');
            localStorage.removeItem('linkedMarketplacesList');
            localStorage.removeItem('fcmToken');
            localStorage.removeItem('featureFlags');
            localStorage.removeItem('subscription');
            localStorage.removeItem('agentSubscription');
            localStorage.removeItem('tenantSettings');
            localStorage.removeItem('registration_status');
            deleteCookie('token');
            deleteCookie('X-Tenant-Id');
            deleteCookie('linker_session');
            deleteCookie('XSRF-TOKEN');
			}
		},
		async getIdToken() {
			const auth = getAuth();
         console.log('[AUTH_STORE] getIdToken auth', auth.currentUser);
			if (auth.currentUser) {
				const idToken = await auth.currentUser.getIdToken();
            console.log('[AUTH_STORE] getIdToken idToken', idToken);
				return idToken;
			}
         console.log('[AUTH_STORE] getIdToken no currentUser');
			await new Promise((resolve) => setTimeout(resolve, 1000));
			return this.getIdToken();
		},
	},
});
